import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, RefreshCw } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import api from '../../services/api';

const RegionsTab = () => {
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentRegion, setCurrentRegion] = useState({ name: '', country_id: '' });
  const [errors, setErrors] = useState({});

  // Pagination & Search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchRegions();
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    if (open) fetchCountries();
  }, [open]);

  const fetchCountries = async () => {
    try {
      // Use all=true to get all countries for dropdown
      const response = await api.get('/countries?all=true');
      setCountries(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page, 
        limit: rowsPerPage,
        search: debouncedSearch
      });
      const response = await api.get(`/regions?${queryParams}`);
      setRegions(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      toast.error('Errore nel caricamento delle regioni');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (region = null) => {
    if (region) {
      setEditMode(true);
      setCurrentRegion(region);
    } else {
      setEditMode(false);
      // Default to Italy if exists, otherwise first country
      const defaultCountry = countries.find(c => c.name === 'Italia') || countries[0];
      setCurrentRegion({ name: '', country_id: defaultCountry ? defaultCountry.id : '' });
    }
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentRegion({ name: '', country_id: '' });
  };

  const validate = () => {
    let tempErrors = {};
    if (!currentRegion.name) tempErrors.name = 'Il nome è obbligatorio';
    if (!currentRegion.country_id) tempErrors.country_id = 'La nazione è obbligatoria';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (editMode) {
        await api.put(`/regions/${currentRegion.id}`, currentRegion);
      } else {
        await api.post('/regions', currentRegion);
      }

      toast.success(editMode ? 'Regione aggiornata' : 'Regione creata');
      fetchRegions();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.msg || error.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (region) => {
    setDeleteTarget(region);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/regions/${deleteTarget.id}`);
      toast.success('Regione eliminata');
      closeDeleteDialog();
      fetchRegions();
    } catch (error) {
      console.error(error);
      toast.error('Impossibile eliminare: probabilmente ci sono dati collegati');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Cerca regione..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => handleOpen()}
        >
          Nuova Regione
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Nazione</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" className="py-8">
                  <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                </TableCell>
              </TableRow>
            ) : (
              regions.map((region) => (
                <TableRow key={region.id}>
                  <TableCell>{region.name}</TableCell>
                  <TableCell>{region.country_name}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica">
                      <IconButton size="small" onClick={() => handleOpen(region)} color="primary">
                        <EditIcon size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton size="small" onClick={() => openDeleteDialog(region)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editMode ? 'Modifica Regione' : 'Nuova Regione'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome Regione"
            type="text"
            fullWidth
            value={currentRegion.name}
            onChange={(e) => setCurrentRegion({ ...currentRegion, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth error={!!errors.country_id}>
            <InputLabel>Nazione</InputLabel>
            <Select
              value={currentRegion.country_id}
              label="Nazione"
              onChange={(e) => setCurrentRegion({ ...currentRegion, country_id: e.target.value })}
            >
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button onClick={handleSubmit} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile e può coinvolgere dati collegati. Stai per eliminare la regione:
          </DialogContentText>
          <Box sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            Nazione: {deleteTarget?.country_name || '-'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegionsTab;
